import React, { useEffect, useRef, useState } from "react";
import AdminHeader from "./components/header";
import Sidebar from "./components/sidebar";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import "./app.scss";
import { getUrlPath } from "@src/utils/app_functions";
import Header from "@src/acquisition/components/header";
import useAuthContext from "@src/hooks/useAuthContext";
import Loading from "@src/components/Loading";

const AppLayout = () => {
  const [isAffiliate, setIsAffiliate] = React.useState(false);
  const [schoolAdminPageName, setSchoolAdminPageName] =
    React.useState("Dashboard");
  const [showInput, setShowInput] = React.useState(true);
  const [value, setValue] = React.useState("");
  const [menu, setMenu] = React.useState<React.ReactElement | undefined>(
    undefined
  );
  const [selected, setSelected] = React.useState<string>("");
  const [sidebarCollapse, setSidebarCollapse] = React.useState(false);

  const [scale, setScale] = React.useState(1);
  const path = getUrlPath();
  const location = useLocation();
  const [isRoot, setIsRoot] = useState<boolean>(location.pathname === "/");
  const [isFeatures, setIsFeatures] = useState<boolean>(
    location.pathname === "/features"
  );
  const faqRef = useRef<HTMLDivElement | null>(null);
  const featuresRef = useRef<HTMLDivElement | null>(null);
  // Below Tailwind's md breakpoint the sidebar becomes a drawer opened from the header.
  const mobileQuery = "(max-width: 767px)";
  const [isMobile, setIsMobile] = useState(() => window.matchMedia(mobileQuery).matches);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [showFrame, setShowFrame] = useState<boolean>(false);
  const [editorView, setEditorView] = useState<boolean>(false);
  const [isHome, setIsHome] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLanded, setIsLanded] = useState(false);
  const { userProfile, isLoading, isAuthenticated } = useAuthContext();

  useEffect(() => {
    const media = window.matchMedia(mobileQuery);
    const onChange = (e: MediaQueryListEvent) => {
      setIsMobile(e.matches);
      if (!e.matches) setMobileSidebarOpen(false);
    };
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  // close the drawer once a page is picked
  useEffect(() => {
    setMobileSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileSidebarOpen) return;
    const onKeyDown = (e: KeyboardEvent) => e.key === "Escape" && setMobileSidebarOpen(false);
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [mobileSidebarOpen]);
  const [userDetails, setUserDetails] = useState<AdminDetailsProps | null>(null);

  // Update userDetails when userProfile changes from AuthContext
  useEffect(() => {
    if (userProfile) {
      setUserDetails(userProfile);
      // You can also store in localStorage if needed
      localStorage.setItem('user', JSON.stringify(userProfile));
    }
  }, [userProfile]);

  useEffect(() => {
    const page = getUrlPath().pagename;
    if (page == "") {
      setIsHome(true);
    } else {
      setIsHome(false);
    }
  }, [window.location.pathname]);

  useEffect(() => {
    const urlDetails = getUrlPath();
    if (urlDetails.pagename.toLowerCase() === "pricing") {
      setShowFrame(true);
    } else if (urlDetails.pagename.toLowerCase() === "editor") {
      setEditorView(true);
    } else {
      setShowFrame(false);
    }

    setIsRoot(location.pathname === "/");
    setIsFeatures(location.pathname === "/features");
  }, [window.location.pathname]);

  
  const scrollToSection = (ref: React.RefObject<HTMLDivElement>) => {
    ref.current?.scrollIntoView({ behavior: "smooth" });
  };
  const navigate = useNavigate();


  if (isLoading && window.location.pathname.includes('/home/')) {
    return <Loading />;
  }


  return (
    <div className=" overflow-hidden  h-screen margin-auto flex">
      {!isLanded && isMobile && (
        <div
          className={`fixed inset-0 z-40 bg-black/40 transition-opacity duration-200 ${mobileSidebarOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
          onClick={() => setMobileSidebarOpen(false)}
          aria-hidden="true"
        />
      )}
      <aside
        className={
          isMobile
            ? `fixed inset-y-0 left-0 z-50 h-screen transition-transform duration-200 ${mobileSidebarOpen ? "translate-x-0" : "-translate-x-full"}`
            : "h-screen shrink-0"
        }
      >
        {!isLanded && (
          <Sidebar
            selected={(v: string) => {
              localStorage.setItem("activeModule", v);
              setSelected(v);
            }}
            userDetails={userDetails}
            style={{
              width: isMobile ? "min(16.25rem, 85vw)" : sidebarCollapse ? "5vw" : "16.25rem",
            }}
            collapsed={(b: boolean) => {
              setSidebarCollapse(b);
            }}
            isMobile={isMobile}
            onMobileClose={() => setMobileSidebarOpen(false)}
            className="h-full"
          />
        )}
      </aside>

      <div className="flex flex-col flex-1 min-w-0">
        <header>
          {isLanded ? (
            <Header
              onClickFaQ={() => scrollToSection(faqRef)}
              onClickFeature={() =>
                isFeatures
                  ? scrollToSection(featuresRef)
                  : navigate("/features")
              }
              onClickPricing={() =>
                isLoggedIn
                  ? navigate("/dashboard/pricing")
                  : navigate("/pricing")
              }
              isLoggedIn={isLoggedIn ?? false}
              isPromo={false}
            />
          ) : (
            <AdminHeader
              menu={menu}
              schoolAdminPageName={schoolAdminPageName}
              showInput={showInput}
              value={value}
              setValue={setValue}
              isAffiliate={isAffiliate}
              onMenuClick={isMobile ? () => setMobileSidebarOpen(true) : undefined}
            />
          )}
        </header>

        <main className=" w-full overflow-auto h-screen bg-gray-50 ">
          <Outlet
            context={{
              setSchoolAdminPageName,
              setShowInput,
              setValue,
              setMenu,
              setIsAffiliate,
              value,
              setIsLanded,
              userDetails
            }}
          />
        </main>
      </div>
    </div>
  );
};

export default AppLayout;
